import { useEffect, useRef } from "react";
import { useBoundStore } from "store/store";

export const useTabCloseLogout = () => {
  const setIsLoggedIn = useBoundStore((state) => state.setIsLoggedIn);
  const hasLoggedOutRef = useRef(false); // 🔒 Prevent infinite logout loop

  const clearAllSessionData = () => {
    if (hasLoggedOutRef.current) return; // ✅ already handled
    hasLoggedOutRef.current = true;

    console.log("🧹 Clearing session & localStorage (single execution)");
    
    try {
      sessionStorage.clear();
      localStorage.removeItem("tabsOpen"); // optional cleanup
      sessionStorage.removeItem("user_refresh_token_object");
      localStorage.removeItem("user");
      setIsLoggedIn(false);
      window.location.href = "/login";
    } catch (err) {
      console.error("Error clearing session:", err);
    }
  };

  const broadcastLogout = () => {
    try {
      // ✅ only broadcast once
      if (!hasLoggedOutRef.current) {
        hasLoggedOutRef.current = true;
        localStorage.setItem("forceLogout", Date.now().toString());
      }
    } catch (err) {
      console.error("Error broadcasting logout:", err);
    }
  };

  const handleTabClose = () => {
    try {
      const tabsOpenRaw = localStorage.getItem("tabsOpen");
      const tabsOpen = tabsOpenRaw ? parseInt(tabsOpenRaw) : 0;

      if (tabsOpen <= 1) {
        console.log("🚪 Last tab closed — broadcasting logout once");
        broadcastLogout();
      }
      localStorage.setItem("tabsOpen", Math.max(tabsOpen - 1, 0).toString());
    } catch (err) {
      console.error("Error in handleTabClose:", err);
    }
  };

  useEffect(() => {
    // Increment tab count
    const tabsOpenRaw = localStorage.getItem("tabsOpen");
    const tabsOpen = tabsOpenRaw ? parseInt(tabsOpenRaw) : 0;
    localStorage.setItem("tabsOpen", (tabsOpen + 1).toString());

    // --- Event Handlers ---
    const onPageHide = (e: PageTransitionEvent) => {
      if (!e.persisted) handleTabClose();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") handleTabClose();
    };

    const onBeforeUnload = () => handleTabClose();

    // --- Listen for logout from other tabs ---
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === "forceLogout" && !hasLoggedOutRef.current) {
        console.log("🔒 Logout detected via storage event");
        clearAllSessionData();
      }
    };

    // --- Register listeners ---
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("storage", onStorageChange);

    // --- Cleanup ---
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("storage", onStorageChange);
    };
  }, [setIsLoggedIn]);
};
